import { createContext, useContext, useState, ReactNode, FC } from "react";
import { PromptResponse } from "./types";

interface PromptLibraryContextType {
  isCreateOrEditPrompt: boolean;
  promptToEdit: PromptResponse | undefined;
  prefilledPromptText: string;
  openCreatePrompt: () => void;
  openEditPrompt: (prompt: PromptResponse) => void;
  openWithPromptText: (promptText: string) => void;
  closePromptForm: () => void;
}

const PromptLibraryContext = createContext<PromptLibraryContextType | undefined>(undefined);

export const usePromptLibrary = () => {
  const context = useContext(PromptLibraryContext);
  if (!context) {
    throw new Error("usePromptLibrary must be used within PromptLibraryProvider");
  }
  return context;
};

interface PromptLibraryProviderProps {
  children: ReactNode;
}

export const PromptLibraryProvider: FC<PromptLibraryProviderProps> = ({ children }) => {
  const [isCreateOrEditPrompt, setIsCreateOrEditPrompt] = useState(false);
  const [promptToEdit, setPromptToEdit] = useState<PromptResponse | undefined>();
  const [prefilledPromptText, setPrefilledPromptText] = useState<string>("");

  const openCreatePrompt = () => {
    setPrefilledPromptText("");
    setPromptToEdit(undefined);
    setIsCreateOrEditPrompt(true);
  };

  const openEditPrompt = (prompt: PromptResponse) => {
    setPromptToEdit(prompt);
    setPrefilledPromptText("");
    setIsCreateOrEditPrompt(true);
  };

  const openWithPromptText = (promptText: string) => {
    setPrefilledPromptText(promptText);
    setPromptToEdit(undefined);
    setIsCreateOrEditPrompt(true);
  };

  const closePromptForm = () => {
    setPromptToEdit(undefined);
    setPrefilledPromptText("");
    setIsCreateOrEditPrompt(false);
  };

  return (
    <PromptLibraryContext.Provider
      value={{
        isCreateOrEditPrompt,
        promptToEdit,
        prefilledPromptText,
        openCreatePrompt,
        openEditPrompt,
        openWithPromptText,
        closePromptForm,
      }}
    >
      {children}
    </PromptLibraryContext.Provider>
  );
};
